import cx from "classnames"

import Button from "../Buttons/Button"
import { ChevronLeftIcon, ChevronRightIcon, DotsHorizontalIcon } from "../Icon"

const PaginationButton = ({
  currentPage,
  pageNumber,
  onPageChange,
}: {
  currentPage: number
  pageNumber: number
  onPageChange: (page: number) => void
}) => {
  const isActive = currentPage === pageNumber
  return (
    <Button
      className={cx("cobalt-pagination__Button")}
      selected={isActive}
      onClick={() => !isActive && onPageChange(pageNumber)}
    >
      {pageNumber}
    </Button>
  )
}

const PAGE_DOTS_VALUE = -1 // Unrevelant value only used to represent dots in the pages array

const generatePages = (
  currentPage: number,
  totalPages: number,
  options: {
    maxVisibleLength: number
    edgeVisibleLength: number
  },
) => {
  const pages: number[] = []
  const maxVisiblePages = options.maxVisibleLength // Max visible pages (excluding dots)
  const edgeButtons = options.edgeVisibleLength // Number of fixed edge buttons

  if (totalPages <= maxVisiblePages) {
    // Show all pages
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i)
    }
    return pages
  }

  // Calculate dynamic range for middle pages
  const middlePagesCount = maxVisiblePages - 2 * edgeButtons // Remaining pages for the middle
  let startMiddlePage = Math.max(
    currentPage - Math.floor(middlePagesCount / 2),
    edgeButtons + 1,
  )
  let endMiddlePage = Math.min(
    currentPage + Math.floor(middlePagesCount / 2),
    totalPages - edgeButtons,
  )

  // Adjust start and end pages if they exceed boundaries
  if (startMiddlePage === edgeButtons + 1) {
    endMiddlePage = startMiddlePage + middlePagesCount - 1
  } else if (endMiddlePage === totalPages - edgeButtons) {
    startMiddlePage = endMiddlePage - middlePagesCount + 1
  }

  // Add leading edge buttons
  for (let i = 1; i <= edgeButtons; i++) {
    pages.push(i)
  }
  if (startMiddlePage > edgeButtons + 1) {
    pages.push(PAGE_DOTS_VALUE)
  }

  // Add middle pages
  for (let i = startMiddlePage; i <= endMiddlePage; i++) {
    pages.push(i)
  }

  // Add trailing edge buttons
  if (endMiddlePage < totalPages - edgeButtons) {
    pages.push(PAGE_DOTS_VALUE)
  }
  for (let i = totalPages - edgeButtons + 1; i <= totalPages; i++) {
    pages.push(i)
  }

  return pages
}

const Pagination = ({
  onPageChange,
  currentPage,
  totalPages,
  options = {
    maxVisibleLength: 11,
    edgeVisibleLength: 3,
  },
}: {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  options?: {
    maxVisibleLength: number
    edgeVisibleLength: number
  }
}) => {
  if (totalPages <= 1) return null
  const pages = generatePages(currentPage, totalPages, options)

  return (
    <div className="cobalt-pagination">
      <Button
        className={cx("cobalt-pagination__Button")}
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
        icon={<ChevronLeftIcon />}
      />
      {pages.map((page) =>
        page === PAGE_DOTS_VALUE ? (
          <DotsHorizontalIcon
            key={`dots-${page}`}
            className="cobalt-pagination__threeDots"
            color="primary"
          />
        ) : (
          <PaginationButton
            key={page}
            currentPage={currentPage}
            pageNumber={page}
            onPageChange={onPageChange}
          />
        ),
      )}
      <Button
        className={cx("cobalt-pagination__Button")}
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        icon={<ChevronRightIcon />}
      />
    </div>
  )
}

export default Pagination
